import type { CryptoSwapsTranslations } from "@/shared/lang/interfaces/cryptoSwaps";

import type { OtcSellOrderStatus } from "../types/cryptoSell.types";
import type { OtcAmountAnchor, OtcOrderStatus } from "../types/cryptoSwaps.types";

export const QUOTE_DEBOUNCE_MS = 600;

/** The limits line under each amount field: the purchase's are per purchase, the sale's per sale. */
export type LimitHintKeys = Record<
  OtcAmountAnchor,
  Record<"both" | "min" | "max", keyof CryptoSwapsTranslations>
>;

export const BUY_LIMIT_KEYS: LimitHintKeys = {
  send: { both: "send_limits_hint", min: "send_min_hint", max: "send_max_hint" },
  receive: { both: "receive_limits_hint", min: "receive_min_hint", max: "receive_max_hint" },
};

export const SELL_LIMIT_KEYS: LimitHintKeys = {
  send: { both: "sell_send_limits_hint", min: "sell_send_min_hint", max: "sell_send_max_hint" },
  receive: BUY_LIMIT_KEYS.receive,
};

/** The pair catalog is an availability feed, so it is memoized for a minute, not a session. */
export const PAIRS_CACHE_MS = 60_000;

/** The last slice of a quote's life counts as expired: redeeming at T−2 s reaches the server too late. */
export const QUOTE_SUBMIT_GUARD_MS = 10_000;

/** The deposit address is withdrawn this early, so a slow browser clock or a last-second send never misses. */
export const DEPOSIT_ADDRESS_SAFETY_MARGIN_MS = 60_000;

export const QUOTE_CRITICAL_MS = 10_000;

export const QUOTE_WARN_MAX_MS = 30_000;

/** After this many consecutive pricing failures the form stops quoting on its own and waits for a click. */
export const MAX_CONSECUTIVE_QUOTE_FAILURES = 3;

/** Prices refreshed on expiry without the user touching the form; past this an idle tab stops asking. */
export const MAX_AUTO_REFRESHES = 5;

export const ORDER_POLL_INTERVAL_MS = 6_000;

export const ORDERS_POLL_INTERVAL_MS = 15_000;

export const ORDERS_PAGE_SIZE = 10;

export const QUOTE_ANNOUNCE_AT_SECONDS: readonly number[] = [30, 10];

export const CONCEPT_MAX_LENGTH = 120;

/** `send_decimals` when the pair catalog omits it. */
export const DEFAULT_SEND_DECIMALS = 0;

/** Display precision where no pair catalog is at hand (an order, the history). Mirrors the catalog. */
export const CURRENCY_DISPLAY_DECIMALS: Record<string, number> = {
  COP: 0,
  USDT: 2,
};

export const FALLBACK_DISPLAY_DECIMALS = 2;

export interface CryptoSwapNetworkRule {
  label: string;
  shortLabel: string;
  /** The token standard wallets show next to the chain. */
  standard: string;
  /** The block explorer, named on its links. */
  explorer: string;
  txUrl: (hash: string) => string;
  /** The USDT contract on the chain, so a look-alike token in the wallet can be told apart. */
  usdtContract: string;
  contractUrl: (contract: string) => string;
  /** Pays a transfer's network fee: the sending wallet needs some besides the USDT. */
  gasAsset: string;
}

/**
 * The networks this UI can take an address for. A code absent from this table renders raw but cannot be
 * chosen: an address we cannot verify is a bet on the customer's funds. Each flow offers only the networks
 * its pair lists.
 */
export const CRYPTO_SWAP_NETWORKS: Record<string, CryptoSwapNetworkRule> = {
  BSC: {
    label: "BNB Smart Chain (BEP-20)",
    shortLabel: "BNB Smart Chain",
    standard: "BEP-20",
    explorer: "BscScan",
    txUrl: (hash) => `https://bscscan.com/tx/${encodeURIComponent(hash)}`,
    usdtContract: "0x55d398326f99059fF775485246999027B3197955",
    contractUrl: (contract) => `https://bscscan.com/token/${encodeURIComponent(contract)}`,
    gasAsset: "BNB",
  },
  POLYGON: {
    label: "Polygon PoS",
    shortLabel: "Polygon",
    standard: "PoS",
    explorer: "PolygonScan",
    txUrl: (hash) => `https://polygonscan.com/tx/${encodeURIComponent(hash)}`,
    usdtContract: "0xc2132D05D31c914a87C6611C10748AEb04B58e8F",
    contractUrl: (contract) => `https://polygonscan.com/token/${encodeURIComponent(contract)}`,
    gasAsset: "POL",
  },
};

export const ASSET_GLYPHS: Record<string, string> = {
  COP: "$",
};

/** How the order ended, or that it has not. */
export type CryptoSwapBucket = "live" | "done" | "failed" | "unknown";

export const BUCKET_PILL_VARIANT: Record<CryptoSwapBucket, "success" | "danger" | "info" | "neutral"> = {
  live: "info",
  done: "success",
  failed: "danger",
  unknown: "neutral",
};

export const NON_TERMINAL_STATUSES: readonly OtcOrderStatus[] = ["CREATED", "PROCESSING"];

export const STATUS_BUCKET: Record<string, CryptoSwapBucket> = {
  CREATED: "live",
  PROCESSING: "live",
  PAID: "done",
  FAILED: "failed",
  CANCELLED: "failed",
  REVERSED: "failed",
};

export const STATUS_LABEL_KEYS: Record<string, keyof CryptoSwapsTranslations> = {
  CREATED: "status_created",
  PROCESSING: "status_processing",
  PAID: "status_paid",
  FAILED: "status_failed",
  CANCELLED: "status_cancelled",
  REVERSED: "status_reversed",
};

export type CryptoSwapFilterId =
  | "all"
  | "created"
  | "processing"
  | "paid"
  | "failed"
  | "cancelled"
  | "reversed";

/** One chip per request: the list endpoint filters on a single status. */
export const HISTORY_FILTERS: {
  id: CryptoSwapFilterId;
  labelKey: keyof CryptoSwapsTranslations;
  status: OtcOrderStatus | null;
}[] = [
  { id: "all", labelKey: "filter_all", status: null },
  { id: "processing", labelKey: "status_processing", status: "PROCESSING" },
  { id: "created", labelKey: "status_created", status: "CREATED" },
  { id: "paid", labelKey: "status_paid", status: "PAID" },
  { id: "failed", labelKey: "status_failed", status: "FAILED" },
  { id: "cancelled", labelKey: "status_cancelled", status: "CANCELLED" },
  { id: "reversed", labelKey: "status_reversed", status: "REVERSED" },
];

export const UNAVAILABLE_REASON_KEYS: Record<string, keyof CryptoSwapsTranslations> = {
  not_configured: "pair_unavailable_not_configured",
  no_fee_config: "pair_unavailable_not_configured",
  rail_disabled: "pair_unavailable_disabled",
  breb_key_required: "pair_unavailable_breb_key",
  otc_not_enabled: "error_otc_not_enabled",
};

export const BREB_KEY_REQUIRED_REASON = "breb_key_required";
export const RAIL_DISABLED_REASON = "rail_disabled";

export const SAVED_WALLET_ALIAS_MAX_LENGTH = 60;

/** Sales whose page and rows keep polling: the desk or the collection can still move them. */
export const SELL_NON_TERMINAL_STATUSES: readonly OtcSellOrderStatus[] = [
  "AWAITING_DEPOSIT",
  "PROCESSING",
  "COP_SENT",
  "REFUNDING",
];

export type SellPillVariant = "success" | "warning" | "info" | "danger" | "neutral";

/** Waiting on the organization reads amber; an expiry moved nothing, so it reads neutral, not failed. */
export const SELL_STATUS_PILL: Record<string, SellPillVariant> = {
  AWAITING_DEPOSIT: "warning",
  PROCESSING: "info",
  COP_SENT: "info",
  COMPLETED: "success",
  EXPIRED: "neutral",
  REFUNDING: "warning",
  REFUNDED: "danger",
};

export const SELL_STATUS_LABEL_KEYS: Record<string, keyof CryptoSwapsTranslations> = {
  AWAITING_DEPOSIT: "sell_status_awaiting_deposit",
  PROCESSING: "sell_status_processing",
  COP_SENT: "sell_status_cop_sent",
  COMPLETED: "sell_status_completed",
  EXPIRED: "sell_status_expired",
  REFUNDING: "sell_status_refunding",
  REFUNDED: "sell_status_refunded",
};

export type CryptoSellFilterId =
  | "all"
  | "awaiting_deposit"
  | "processing"
  | "cop_sent"
  | "completed"
  | "expired"
  | "refunding"
  | "refunded";

export const SELL_HISTORY_FILTERS: {
  id: CryptoSellFilterId;
  labelKey: keyof CryptoSwapsTranslations;
  status: OtcSellOrderStatus | null;
}[] = [
  { id: "all", labelKey: "filter_all", status: null },
  { id: "awaiting_deposit", labelKey: "sell_status_awaiting_deposit", status: "AWAITING_DEPOSIT" },
  { id: "processing", labelKey: "sell_status_processing", status: "PROCESSING" },
  { id: "cop_sent", labelKey: "sell_status_cop_sent", status: "COP_SENT" },
  { id: "completed", labelKey: "sell_status_completed", status: "COMPLETED" },
  { id: "expired", labelKey: "sell_status_expired", status: "EXPIRED" },
  { id: "refunding", labelKey: "sell_status_refunding", status: "REFUNDING" },
  { id: "refunded", labelKey: "sell_status_refunded", status: "REFUNDED" },
];

export const SELL_UNAVAILABLE_REASON_KEYS: Record<string, keyof CryptoSwapsTranslations> = {
  not_configured: "sell_unavailable_not_configured",
  no_fee_config: "sell_unavailable_not_configured",
  rail_disabled: "sell_unavailable_disabled",
  breb_key_required: "sell_unavailable_breb_key",
  otc_not_enabled: "error_otc_not_enabled",
};

/** Why a sale did not complete, by the reason the backend relays; anything else reads as the generic one. */
export const SELL_FAILURE_REASON_KEYS: Record<string, keyof CryptoSwapsTranslations> = {
  payment_timeout: "sell_reason_payment_timeout",
  kyt_rejected: "sell_reason_compliance",
  compliance_rejected: "sell_reason_compliance",
  compliance_timeout: "sell_reason_compliance",
  limit_exceeded: "sell_reason_limit",
  beneficiary_mismatch: "sell_reason_payout_failed",
  breb_key_not_found: "sell_reason_payout_failed",
  payout_failed: "sell_reason_payout_failed",
  refund_destination_required: "sell_reason_refund_pending",
  refund_destination_invalid: "sell_reason_refund_pending",
};
